// Sign-in with modes (runtime `signInModes` flag, via SignInRoute): email code, password, passkey.
import React, { useEffect, useRef, useState } from "react";
import {
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoClient } from "../aws/cognitoClient";
import "../styles/SignIn.css";
import { useLocation, useNavigate } from "react-router-dom";
import { consumeReturnTo } from "../helpers/returnTo";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../context/AuthContext";
import { getDisplayNameFromToken } from "../helpers/userNameHelper";
import { useTranslation } from "../context/TranslationContext";
import {
  storeRefreshToken,
  fetchPasswordStatus,
  createPassword,
  skipPasswordPrompt,
  sendPortalEvent,
  generateLoginAttemptId,
  PORTAL_EVENT_TYPES,
} from "../helpers/apiHelper";
import {
  isPasswordValid,
  getPasswordRules,
  isValidEmail,
} from "../helpers/validationHelper";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Modal from "./Modal";
import SignInHelpForm from "./SignInHelpForm";  
import { getRememberedEmail, saveRememberedEmail } from "../helpers/rememberedEmailHelper";
import {
  canUsePasskeys,
  getPasskeyAssertion,
  hasPasskeyOptIn,
  isPasskeyCancelledError,
} from "../helpers/passkeys";
import { SIGN_IN_METHOD_MODES } from "../helpers/signInModes";
import "../styles/Views.css";

// Cognito: passwordless EMAIL_OTP first-factor codes have 8 digits; email MFA after a
// password and ForgotPassword confirmation codes have 6.
const OTP_LENGTH = 6;
const FIRST_FACTOR_OTP_LENGTH = 8;
const OTP_CODE_ERRORS = new Set(["CodeMismatchException", "ExpiredCodeException"]);
const COGNITO_OTP_VALIDITY_MS = 3 * 60 * 1000;
// A WEB_AUTHN challenge kept after a cancelled prompt is reused only while Cognito's session is fresh.
const PASSKEY_CHALLENGE_REUSE_MS = 2 * 60 * 1000;
const PASSKEY_FALLBACK = "EMAIL_CODE";

// Sign-in screen variants before a challenge: email code (default), password, forgot password.
const MODES = Object.freeze({ CODE: "code", PASSWORD: "password", FORGOT: "forgot" });
const SIGN_IN_METHODS = Object.freeze({
  EMAIL_CODE: "EMAIL_CODE",
  PASSWORD: "PASSWORD",
  PASSKEY: "PASSKEY",
});
const PASSWORD_PROMPT_REASONS = Object.freeze({
  FIRST_ACCESS: "FIRST_ACCESS",
  PASSWORD_RESET: "PASSWORD_RESET",
});

const PASSWORD_CHALLENGES = ["PASSWORD", "PASSWORD_SRP"];

// USER_AUTH without EMAIL_OTP among the first factors: the account signs in with a
// password. Only PASSWORD_AND_CODE accounts (email MFA) lose it.
const isPasswordOnlyFirstFactor = (response) => {
  if (response?.ChallengeName !== "SELECT_CHALLENGE") return false;
  const available = response.AvailableChallenges || [];
  return !available.includes("EMAIL_OTP") &&
    available.some((name) => PASSWORD_CHALLENGES.includes(name));
};

const FORGOT_PASSWORD_ERRORS = {
  CodeMismatchException: "signIn.resetCodeInvalid",
  ExpiredCodeException: "signIn.resetCodeExpired",
  InvalidPasswordException: "signIn.createPasswordInvalid",
  LimitExceededException: "signIn.resetTooManyAttempts",
  TooManyRequestsException: "signIn.resetTooManyAttempts",
  TooManyFailedAttemptsException: "signIn.resetTooManyAttempts",
};

const normaliseOtp = (value, length = OTP_LENGTH) =>
  String(value ?? "").replace(/\D/g, "").slice(0, length);

const formatCountdown = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const getErrorCode = (err) => err?.name || err?.code || "UnknownError";

const SegmentedOtpInput = ({ value, onChange, idPrefix, label, length = OTP_LENGTH }) => {
  const inputRefs = useRef([]);
  const digits = Array.from({ length: length }, (_, index) => value[index] || "");

  useEffect(() => {
    const firstEmpty = Array.from(
      { length: length },
      (_, index) => value[index] || "",
    ).findIndex((digit) => !digit);
    inputRefs.current[firstEmpty === -1 ? length - 1 : firstEmpty]?.focus();
  }, [value, length]);

  const updateDigits = (nextDigits) => onChange(normaliseOtp(nextDigits.join(""), length));

  const fillDigits = (index, rawValue) => {
    const incomingDigits = normaliseOtp(rawValue, length).split("");
    if (!incomingDigits.length) return;

    const nextDigits = [...digits];
    incomingDigits.slice(0, length - index).forEach((digit, offset) => {
      nextDigits[index + offset] = digit;
    });
    updateDigits(nextDigits);
    inputRefs.current[Math.min(index + incomingDigits.length, length - 1)]?.focus();
  };

  const handleChange = (index, rawValue) => {
    const incomingDigits = normaliseOtp(rawValue, length);
    if (!incomingDigits) {
      const nextDigits = [...digits];
      nextDigits[index] = "";
      updateDigits(nextDigits);
      return;
    }

    // The first box accepts the whole code (one-time-code autofill); typing over a
    // filled box keeps only the new digit.
    if (incomingDigits.length === 2 && digits[index] && incomingDigits.startsWith(digits[index])) {
      const nextDigits = [...digits];
      nextDigits[index] = incomingDigits[1];
      updateDigits(nextDigits);
      inputRefs.current[Math.min(index + 1, length - 1)]?.focus();
      return;
    }

    if (incomingDigits.length > 1) {
      fillDigits(index, incomingDigits);
      return;
    }

    const nextDigits = [...digits];
    nextDigits[index] = incomingDigits;
    updateDigits(nextDigits);
    inputRefs.current[Math.min(index + 1, length - 1)]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const nextDigits = [...digits];
      if (nextDigits[index]) {
        nextDigits[index] = "";
        updateDigits(nextDigits);
      } else if (index > 0) {
        nextDigits[index - 1] = "";
        updateDigits(nextDigits);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  return (
    <div className="otp-field">
      <label className="otp-label" htmlFor={`${idPrefix}-0`}>{label}</label>
      <div className={`otp-inputs otp-inputs--${length}`} aria-label={label}>
        {digits.map((digit, index) => (
          <input
            key={`${idPrefix}-${index}`}
            id={`${idPrefix}-${index}`}
            ref={(element) => { inputRefs.current[index] = element; }}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            className="otp-input"
            maxLength={index === 0 ? length : 1}
            value={digit}
            aria-label={`${label}: digit ${index + 1}`}
            onInput={(event) => handleChange(index, event.currentTarget.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => {
              const pastedValue = normaliseOtp(event.clipboardData?.getData("text"), length);
              if (!pastedValue) return;
              event.preventDefault();
              fillDigits(index, pastedValue);
            }}
          />
        ))}
      </div>
    </div>
  );
};

const SignIn = () => {
  const location = useLocation();
  const [email, setEmail] = useState(() => getRememberedEmail());
  const [rememberEmail, setRememberEmail] = useState(() => Boolean(getRememberedEmail()));
  const [mode, setMode] = useState(MODES.CODE);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpLength, setOtpLength] = useState(OTP_LENGTH);

  const [challenge, setChallenge] = useState(null);
  // { tokens, reason }: shown after an email-code sign-in, before entering the portal.
  const [passwordPrompt, setPasswordPrompt] = useState(null);
  // Cognito ForgotPassword for accounts without email-code sign-in: { sentAt }.
  const [passwordReset, setPasswordReset] = useState(null);
  const [resetCode, setResetCode] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpNow, setOtpNow] = useState(Date.now());

  const [resendSuccess, setResendSuccess] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // { email, response, startedAt }: lets a retry after a cancelled prompt open it straight away.
  const passkeyChallengeRef = useRef(null);

  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { t, language } = useTranslation();

  const loginAttemptIdRef = useRef(null);
  // How the current attempt started; drives events, resend and the create-password prompt.
  const signInMethodRef = useRef(null);
  const fromForgotPasswordRef = useRef(false);
  // Blocks a second submit (Enter, click or OTP auto-submit) before the
  // `loading` state has re-rendered the disabled buttons.
  const submittingRef = useRef(false);
  const challengeStartedAtRef = useRef(null);

  const attemptMetadata = () => ({
    signInMethod: signInMethodRef.current || undefined,
    flow: fromForgotPasswordRef.current ? "PASSWORD_RESET" : undefined,
  });

  const trackEvent = (eventType, { metadata, idToken, email: eventEmail } = {}) => {
    sendPortalEvent(eventType, {
      loginAttemptId: loginAttemptIdRef.current,
      email: idToken ? undefined : eventEmail ?? email,
      idToken,
      metadata: { language, ...attemptMetadata(), ...metadata },
    });
  };

  const trackChallenge = (response) => {
    challengeStartedAtRef.current = Date.now();
    if (response.ChallengeName === "EMAIL_OTP") {
      trackEvent(
        fromForgotPasswordRef.current && signInMethodRef.current === SIGN_IN_METHODS.EMAIL_CODE
          ? PORTAL_EVENT_TYPES.PASSWORD_RESET_CODE_SENT
          : PORTAL_EVENT_TYPES.OTP_SENT,
        { metadata: { method: "EMAIL_OTP", challenge: "EMAIL_OTP" } },
      );
    } else if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
      trackEvent(PORTAL_EVENT_TYPES.NEW_PASSWORD_REQUIRED, {
        metadata: { challenge: "NEW_PASSWORD_REQUIRED" },
      });
    }
  };

  const challengeAgeSeconds = () =>
    challengeStartedAtRef.current
      ? Math.round((Date.now() - challengeStartedAtRef.current) / 1000)
      : undefined;

  const trackLoginFailed = (step, err, extraMetadata = {}, idToken) => {
    trackEvent(PORTAL_EVENT_TYPES.LOGIN_FAILED, {
      idToken,
      metadata: {
        step,
        errorCode: err ? getErrorCode(err) : undefined,
        errorMessage: err?.message,
        challengeAgeSeconds: challengeAgeSeconds(),
        ...extraMetadata,
      },
    });
  };

  const startAttempt = (method, { forgotPassword = false } = {}) => {
    loginAttemptIdRef.current = generateLoginAttemptId();
    signInMethodRef.current = method;
    fromForgotPasswordRef.current = forgotPassword;
    challengeStartedAtRef.current = null;
  };

  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

  const passwordRules = getPasswordRules(newPassword);
  const allPasswordRulesMet = isPasswordValid(newPassword);

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const passwordsMatch =
    newPassword.length > 0 && newPassword === confirmNewPassword;

  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const showPasskeyOption =
    mode === MODES.CODE && canUsePasskeys(true) && isValidEmail(email) && hasPasskeyOptIn(email);
  const otpRemainingMs = otpExpiresAt ? Math.max(0, otpExpiresAt - otpNow) : 0;
  const otpExpired = otpExpiresAt !== null && otpRemainingMs === 0;

  useEffect(() => {
    if (!otpExpiresAt) return undefined;
    setOtpNow(Date.now());
    const intervalId = window.setInterval(() => setOtpNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [otpExpiresAt]);

  const startOtpTimer = (validityMs) => {
    const now = Date.now();
    setOtpNow(now);
    setOtpExpiresAt(now + validityMs);
  };

  useEffect(() => {
    if (!location.state?.passwordChanged) return;

    setSuccessMessage("signIn.passwordChangedSuccess");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const runGuarded = async (handler, ...args) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await handler(...args);
    } finally {
      submittingRef.current = false;
    }
  };

  const submitWith = (handler, canSubmit = true) => (event) => {
    event.preventDefault();
    if (loading || !canSubmit) return;
    runGuarded(handler);
  };

  const switchMode = (nextMode) => {
    setError("");
    setSuccessMessage("");
    setPassword("");
    setPasswordReset(null);
    setMode(nextMode);
  };

  const showEmailOtpChallenge = (response) => {
    setOtpLength(
      signInMethodRef.current === SIGN_IN_METHODS.EMAIL_CODE ? FIRST_FACTOR_OTP_LENGTH : OTP_LENGTH,
    );
    setChallenge(response);
    startOtpTimer(COGNITO_OTP_VALIDITY_MS);
  };

  // USER_AUTH with EMAIL_OTP as the first factor. Cognito may answer with
  // SELECT_CHALLENGE instead of honouring PREFERRED_CHALLENGE; pick EMAIL_OTP then.
  const initiateEmailCode = async () => {
    const response = await cognitoClient.send(
      new InitiateAuthCommand({
        AuthFlow: "USER_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PREFERRED_CHALLENGE: "EMAIL_OTP",
        },
        ClientMetadata: { loginAttemptId: loginAttemptIdRef.current },
      }),
    );

    if (
      response.ChallengeName === "SELECT_CHALLENGE" &&
      (response.AvailableChallenges || []).includes("EMAIL_OTP")
    ) {
      return cognitoClient.send(
        new RespondToAuthChallengeCommand({
          ClientId: clientId,
          ChallengeName: "SELECT_CHALLENGE",
          ChallengeResponses: { USERNAME: email, ANSWER: "EMAIL_OTP" },
          Session: response.Session,
          ClientMetadata: { loginAttemptId: loginAttemptIdRef.current },
        }),
      );
    }
    return response;
  };

  const handleRequestCode = async () => {
    setError("");
    setSuccessMessage("");
    setChallenge(null);

    if (!isValidEmail(email)) {
      setError("signIn.invalidEmail");
      return;
    }

    const forgotPassword = mode === MODES.FORGOT;
    startAttempt(SIGN_IN_METHODS.EMAIL_CODE, { forgotPassword });
    if (forgotPassword) {
      trackEvent(PORTAL_EVENT_TYPES.PASSWORD_RESET_REQUESTED);
    }

    setLoading(true);
    try {
      const response = await initiateEmailCode();

      if (response.ChallengeName === "EMAIL_OTP") {
        showEmailOtpChallenge(response);
        trackChallenge(response);
      } else if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else if (isPasswordOnlyFirstFactor(response) && forgotPassword) {
        await sendPasswordResetCode();
      } else if (isPasswordOnlyFirstFactor(response)) {
        trackEvent(PORTAL_EVENT_TYPES.LOGIN_FAILED, {
          metadata: { step: "REQUEST_CODE", errorCode: "PasswordSignInRequired" },
        });
        setPassword("");
        setMode(MODES.PASSWORD);
        setSuccessMessage("signIn.accountUsesPassword");
      } else {
        trackLoginFailed("REQUEST_CODE", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.codeUnavailable");
      }
    } catch (err) {
      trackLoginFailed("REQUEST_CODE", err);
      setError("signIn.codeRequestFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    setError("");
    setSuccessMessage("");
    setChallenge(null);

    if (!isValidEmail(email)) {
      setError("signIn.invalidEmail");
      return;
    }

    if (!isPasswordValid(password)) {
      setError("signIn.invalidPassword");
      return;
    }

    startAttempt(SIGN_IN_METHODS.PASSWORD);

    setLoading(true);
    try {
      await passwordSignIn(password);
    } finally {
      setLoading(false);
    }
  };

  // PASSWORD_AND_CODE users (email MFA) get the email code next; users without MFA go straight in.
  const passwordSignIn = async (passwordValue) => {
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: passwordValue,
        },
      });

      const response = await cognitoClient.send(command);

      if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        setChallenge(response);
        trackChallenge(response);
      } else if (response.ChallengeName === "EMAIL_OTP") {
        showEmailOtpChallenge(response);
        trackChallenge(response);
      } else if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else {
        trackLoginFailed("INITIATE_AUTH", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.unexpectedResponse");
      }
    } catch (err) {
      trackLoginFailed("INITIATE_AUTH", err);
      setError("signIn.failed");
    }
  };

  // Cognito ForgotPassword: the code goes by email through the custom sender.
  const sendPasswordResetCode = async () => {
    try {
      await cognitoClient.send(
        new ForgotPasswordCommand({
          ClientId: clientId,
          Username: email,
          ClientMetadata: { loginAttemptId: loginAttemptIdRef.current },
        }),
      );
      challengeStartedAtRef.current = Date.now();
      trackEvent(PORTAL_EVENT_TYPES.PASSWORD_RESET_CODE_SENT, {
        metadata: { method: "FORGOT_PASSWORD" },
      });
      setResetCode("");
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordReset({ sentAt: Date.now() });
    } catch (err) {
      trackLoginFailed("FORGOT_PASSWORD", err);
      const mapped = FORGOT_PASSWORD_ERRORS[getErrorCode(err)];
      setError(mapped === "signIn.resetTooManyAttempts" ? mapped : "signIn.codeRequestFailed");
    }
  };

  const handleResendResetCode = async () => {
    setError("");
    setLoading(true);
    try {
      await sendPasswordResetCode();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPasswordReset = async () => {
    setError("");
    if (resetCode.length !== OTP_LENGTH || !isPasswordValid(newPassword) || newPassword !== confirmNewPassword) {
      setError("signIn.createPasswordInvalid");
      return;
    }

    setLoading(true);
    try {
      await cognitoClient.send(
        new ConfirmForgotPasswordCommand({
          ClientId: clientId,
          Username: email,
          ConfirmationCode: resetCode,
          Password: newPassword,
          ClientMetadata: { loginAttemptId: loginAttemptIdRef.current },
        }),
      );
    } catch (err) {
      trackLoginFailed("CONFIRM_FORGOT_PASSWORD", err);
      setError(FORGOT_PASSWORD_ERRORS[getErrorCode(err)] || "signIn.resetFailed");
      setLoading(false);
      return;
    }

    trackEvent(PORTAL_EVENT_TYPES.PASSWORD_RESET_COMPLETED, {
      metadata: { method: "FORGOT_PASSWORD", challengeAgeSeconds: challengeAgeSeconds() },
    });
    // Same attempt, now signing in with the new password (then the email code).
    const chosenPassword = newPassword;
    signInMethodRef.current = SIGN_IN_METHODS.PASSWORD;
    setPassword(chosenPassword);
    setPasswordReset(null);
    setResetCode("");
    setNewPassword("");
    setConfirmNewPassword("");
    setMode(MODES.PASSWORD);
    setSuccessMessage("signIn.resetCompleted");
    try {
      await passwordSignIn(chosenPassword);
    } finally {
      setLoading(false);
    }
  };

  const getWebAuthnChallenge = async () => {
    const kept = passkeyChallengeRef.current;
    passkeyChallengeRef.current = null;
    if (
      kept &&
      kept.email === email &&
      Date.now() - kept.startedAt < PASSKEY_CHALLENGE_REUSE_MS
    ) {
      return kept;
    }

    const response = await cognitoClient.send(
      new InitiateAuthCommand({
        AuthFlow: "USER_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PREFERRED_CHALLENGE: "WEB_AUTHN",
        },
      }),
    );
    return { email, response, startedAt: Date.now() };
  };

  const handlePasskeySignIn = async () => {
    setError("");
    setSuccessMessage("");
    setChallenge(null);

    if (!isValidEmail(email)) {
      setError("signIn.invalidEmail");
      return;
    }

    startAttempt(SIGN_IN_METHODS.PASSKEY);
    const passkeyMetadata = { method: "PASSKEY", fallback: PASSKEY_FALLBACK };
    let step = "PASSKEY_INITIATE";
    let webAuthn = null;

    setLoading(true);
    setPasskeyBusy(true);
    try {
      webAuthn = await getWebAuthnChallenge();
      challengeStartedAtRef.current = webAuthn.startedAt;
      const { response } = webAuthn;

      if (response.ChallengeName !== "WEB_AUTHN") {
        trackLoginFailed(step, null, {
          ...passkeyMetadata,
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("passkey.signInFailedCode");
        return;
      }

      step = "PASSKEY_ASSERTION";
      const credential = await getPasskeyAssertion(
        response.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS,
      );

      step = "PASSKEY_VERIFY";
      const verifyResponse = await cognitoClient.send(
        new RespondToAuthChallengeCommand({
          ClientId: clientId,
          ChallengeName: "WEB_AUTHN",
          ChallengeResponses: {
            USERNAME: email,
            CREDENTIAL: credential,
          },
          Session: response.Session,
          ClientMetadata: { loginAttemptId: loginAttemptIdRef.current },
        }),
      );

      if (verifyResponse.AuthenticationResult) {
        await handleSuccessfulAuth(verifyResponse.AuthenticationResult, {
          method: "PASSKEY",
        });
      } else {
        trackLoginFailed(step, null, {
          ...passkeyMetadata,
          errorCode: "UnexpectedResponse",
          challenge: verifyResponse.ChallengeName,
        });
        setError("passkey.signInFailedCode");
      }
    } catch (err) {
      const cancelled = isPasskeyCancelledError(err);
      if (cancelled && step === "PASSKEY_ASSERTION") {
        // Safari may refuse the prompt after a network call; the next tap reuses this challenge.
        passkeyChallengeRef.current = webAuthn;
      }
      trackLoginFailed(step, err, {
        ...passkeyMetadata,
        cancelled: cancelled || undefined,
      });
      setError(cancelled ? "passkey.signInCancelledCode" : "passkey.signInFailedCode");
    } finally {
      setLoading(false);
      setPasskeyBusy(false);
    }
  };

  const handleResendOtp = async () => {
    setError("");
    setOtpCode("");
    setResendSuccess(false);

    const isEmailCode = signInMethodRef.current === SIGN_IN_METHODS.EMAIL_CODE;
    if (!isValidEmail(email) || (!isEmailCode && !isPasswordValid(password))) {
      setError("signIn.invalidEmailOrPassword");
      return;
    }

    setLoading(true);
    try {
      const response = isEmailCode
        ? await initiateEmailCode()
        : await cognitoClient.send(
          new InitiateAuthCommand({
            AuthFlow: "USER_PASSWORD_AUTH",
            ClientId: clientId,
            AuthParameters: {
              USERNAME: email,
              PASSWORD: password,
            },
          }),
        );

      if (response.ChallengeName === "EMAIL_OTP") {
        showEmailOtpChallenge(response);
        challengeStartedAtRef.current = Date.now();
        trackEvent(PORTAL_EVENT_TYPES.OTP_RESENT, {
          metadata: { method: "EMAIL_OTP", challenge: "EMAIL_OTP" },
        });
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      } else if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else {
        trackLoginFailed("RESEND_OTP", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.unexpectedResendOtpResponse");
      }
    } catch (err) {
      trackLoginFailed("RESEND_OTP", err);
      setError("signIn.resendOtpFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async () => {
    setError("");

    if (!challenge) {
      setError("signIn.noChallenge");
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError("signIn.invalidNewPassword");
      return;
    }

    setLoading(true);
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: clientId,
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        ChallengeResponses: {
          USERNAME: email,
          NEW_PASSWORD: newPassword,
        },
        Session: challenge.Session,
        ...(loginAttemptIdRef.current
          ? { ClientMetadata: { loginAttemptId: loginAttemptIdRef.current } }
          : {}),
      });

      const response = await cognitoClient.send(command);

      if (response.AuthenticationResult || response.ChallengeName === "EMAIL_OTP") {
        trackEvent(PORTAL_EVENT_TYPES.NEW_PASSWORD_SET, {
          metadata: {
            challenge: "NEW_PASSWORD_REQUIRED",
            challengeAgeSeconds: challengeAgeSeconds(),
          },
        });
      }

      if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else if (response.ChallengeName === "EMAIL_OTP") {
        showEmailOtpChallenge(response);
        trackChallenge(response);
      } else {
        trackLoginFailed("NEW_PASSWORD", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.unexpectedNewPasswordResponse");
      }
    } catch (err) {
      trackLoginFailed("NEW_PASSWORD", err, { challenge: "NEW_PASSWORD_REQUIRED" });
      setError("signIn.newPasswordSubmitFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (code = otpCode) => {
    setError("");

    if (otpExpired) {
      setError("signIn.otpExpired");
      return;
    }

    if (!challenge) {
      setError("signIn.noChallenge");
      return;
    }

    if (!code || code.length < otpLength) {
      setError("signIn.invalidOtpCode");
      return;
    }

    trackEvent(PORTAL_EVENT_TYPES.OTP_SUBMITTED, {
      metadata: {
        method: "EMAIL_OTP",
        challenge: "EMAIL_OTP",
        challengeAgeSeconds: challengeAgeSeconds(),
      },
    });

    setLoading(true);
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: clientId,
        ChallengeName: "EMAIL_OTP",
        ChallengeResponses: {
          USERNAME: email,
          EMAIL_OTP_CODE: code,
        },
        Session: challenge.Session,
        ...(loginAttemptIdRef.current
          ? { ClientMetadata: { loginAttemptId: loginAttemptIdRef.current } }
          : {}),
      });

      const response = await cognitoClient.send(command);

      if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else {
        trackLoginFailed("EMAIL_OTP", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.otpVerificationFailed");
      }
    } catch (err) {
      trackLoginFailed("EMAIL_OTP", err, { method: "EMAIL_OTP", challenge: "EMAIL_OTP" });
      setError(OTP_CODE_ERRORS.has(err?.name) ? "signIn.otpCodeInvalid" : "signIn.otpSubmitFailed");
    } finally {
      setLoading(false);
    }
  };

  // After an email-code sign-in: offer to create a password on first access
  // (no password, never skipped) and always after "Esqueceu a senha?".
  const getPasswordPromptReason = async (idToken) => {
    if (signInMethodRef.current !== SIGN_IN_METHODS.EMAIL_CODE) return null;
    if (fromForgotPasswordRef.current) return PASSWORD_PROMPT_REASONS.PASSWORD_RESET;

    try {
      const { hasPassword, promptSkipped } = await fetchPasswordStatus(idToken);
      return !hasPassword && !promptSkipped ? PASSWORD_PROMPT_REASONS.FIRST_ACCESS : null;
    } catch (err) {
      // The prompt is optional: never block the sign-in on it.
      console.warn("Could not load the password status", err?.status);
      return null;
    }
  };

  const handleSuccessfulAuth = async (tokens, loginMetadata = {}) => {
    try {
      if (tokens.RefreshToken) {
        await storeRefreshToken(tokens.RefreshToken);
      }

      localStorage.setItem("wasLoggedIn", "true");
      saveRememberedEmail(email, rememberEmail);

      trackEvent(PORTAL_EVENT_TYPES.LOGIN_SUCCESS, {
        idToken: tokens.IdToken,
        metadata: { challengeAgeSeconds: challengeAgeSeconds(), ...loginMetadata },
      });

      const promptReason = await getPasswordPromptReason(tokens.IdToken);
      if (promptReason) {
        setChallenge(null);
        setOtpExpiresAt(null);
        setNewPassword("");
        setConfirmNewPassword("");
        setPasswordPrompt({ tokens, reason: promptReason });
        challengeStartedAtRef.current = Date.now();
        trackEvent(PORTAL_EVENT_TYPES.NEW_PASSWORD_REQUIRED, {
          idToken: tokens.IdToken,
          metadata: { challenge: "PASSWORD_PROMPT", reason: promptReason },
        });
        return;
      }

      enterPortal(tokens);
    } catch (err) {
      console.error("Error processing successful auth:", err);
      trackLoginFailed("POST_AUTH", err);
      setError("signIn.clientDataFetchFailed");
    }
  };

  const enterPortal = (tokens) => {
    const decodedToken = jwtDecode(tokens.IdToken);
    const portalUserId = decodedToken["custom:portalUserId"] || null;
    const cocoId = decodedToken["custom:cocoId"] || null;
    const cognitoUserId = decodedToken.sub || null;
    const userData = {
      email,
      username: decodedToken.email || email,
      userId: portalUserId || cognitoUserId || email,
      cocoId,
      portalUserId,
      cognitoUserId,
      authFlow: portalUserId || cognitoUserId ? "portal_user" : "legacy_coco",
      phone_number: decodedToken.phone_number,
      name: getDisplayNameFromToken(decodedToken),
    };

    loginAttemptIdRef.current = null;
    signInMethodRef.current = null;
    fromForgotPasswordRef.current = false;
    challengeStartedAtRef.current = null;

    signIn(userData, tokens);
    navigate(consumeReturnTo() || "/");
    resetForm();
  };

  const handleCreatePassword = async () => {
    setError("");
    if (!passwordPrompt) return;

    if (!isPasswordValid(newPassword) || newPassword !== confirmNewPassword) {
      setError("signIn.createPasswordInvalid");
      return;
    }

    const { tokens, reason } = passwordPrompt;
    const promptMetadata = { challenge: "PASSWORD_PROMPT", reason };
    setLoading(true);
    try {
      await createPassword(newPassword, tokens.IdToken, SIGN_IN_METHOD_MODES.PASSWORD_AND_CODE);
      trackEvent(PORTAL_EVENT_TYPES.NEW_PASSWORD_SET, {
        idToken: tokens.IdToken,
        metadata: {
          ...promptMetadata,
          signInMode: SIGN_IN_METHOD_MODES.PASSWORD_AND_CODE,
          challengeAgeSeconds: challengeAgeSeconds(),
        },
      });
      if (reason === PASSWORD_PROMPT_REASONS.PASSWORD_RESET) {
        trackEvent(PORTAL_EVENT_TYPES.PASSWORD_RESET_COMPLETED, {
          idToken: tokens.IdToken,
          metadata: { challengeAgeSeconds: challengeAgeSeconds() },
        });
      }
      enterPortal(tokens);
    } catch (err) {
      trackLoginFailed(
        "CREATE_PASSWORD",
        err,
        { ...promptMetadata, errorCode: err?.code || getErrorCode(err), status: err?.status },
        tokens.IdToken,
      );
      if (err?.code === "REAUTH_REQUIRED") {
        // The backend only sets a password within 15 minutes of the code sign-in.
        setPasswordPrompt(null);
        resetForm();
        setError("signIn.createPasswordReauth");
      } else {
        setError(
          err?.code === "INVALID_PASSWORD"
            ? "signIn.createPasswordInvalid"
            : err?.code === "PASSKEY_ACTIVE"
              ? "signIn.createPasswordPasskeyActive"
              : "signIn.createPasswordFailed",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPassword = async () => {
    setError("");
    if (!passwordPrompt) return;

    const { tokens } = passwordPrompt;
    setLoading(true);
    try {
      await skipPasswordPrompt(tokens.IdToken);
    } catch (err) {
      // Skipping must never keep the user out; at worst the prompt shows again next time.
      console.warn("Could not record the skipped password prompt", err?.status);
    } finally {
      setLoading(false);
    }
    enterPortal(tokens);
  };

  const handleOtpCodeChange = (value) => {
    setOtpCode(value);
    if (value.length === otpLength && !loading) {
      runGuarded(handleOtpSubmit, value);
    }
  };

  const resetForm = () => {
    setConfirmNewPassword("");
    setChallenge(null);
    setPasswordPrompt(null);
    setPasswordReset(null);
    setResetCode("");
    setNewPassword("");
    setOtpCode("");
    setOtpExpiresAt(null);
  };

  const newPasswordFields = (
    <>
      <div className="password-field">
        <input
          type={showNewPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder={t("signIn.newPasswordPlaceholder")}
          aria-label={t("signIn.newPasswordPlaceholder")}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="portal-auth-input"
        />
        <span
          className="password-toggle-icon"
          onClick={() => setShowNewPassword((prev) => !prev)}
        >
          {showNewPassword ? <FaEyeSlash /> : <FaEye />}
        </span>
      </div>

      <div className="password-field">
        <input
          type={showConfirmNewPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder={t("signIn.confirmNewPasswordPlaceholder")}
          aria-label={t("signIn.confirmNewPasswordPlaceholder")}
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          className="portal-auth-input"
        />
        <span
          className="password-toggle-icon"
          onClick={() => setShowConfirmNewPassword((prev) => !prev)}
        >
          {showConfirmNewPassword ? <FaEyeSlash /> : <FaEye />}
        </span>
      </div>

      {confirmNewPassword.length > 0 && (
        <p
          className={`password-match ${
            passwordsMatch ? "valid" : "invalid"
          }`}
        >
          {passwordsMatch
            ? t("signIn.passwordsMatch")
            : t("signIn.passwordsDoNotMatch")}
        </p>
      )}
    </>
  );

  const showSignInForm = !challenge && !passwordPrompt && !passwordReset;
  const title = () => {
    if (passwordPrompt) return t("signIn.createPasswordTitle");
    if (passwordReset) return t("signIn.passwordRecoveryTitle");
    return mode === MODES.FORGOT ? t("signIn.passwordRecoveryTitle") : t("signIn.title");
  };

  return (
    <div className="signin-container">
      <div className="signin-card">
        {challenge?.ChallengeName !== "EMAIL_OTP" && <h2>{title()}</h2>}
        {error && <p className="error">{t(error)}</p>}
        {successMessage && <p className="success">{t(successMessage)}</p>}

        {showSignInForm && (
          <form
            className="signin-form signin-form--modes"
            onSubmit={submitWith(mode === MODES.PASSWORD ? handleSignIn : handleRequestCode)}
            noValidate
          >
            {mode === MODES.FORGOT && (
              <p className="mb-10">{t("signIn.forgotPasswordIntro")}</p>
            )}

            <input
              type="email"
              name="email"
              autoComplete="username"
              placeholder={t("signIn.emailPlaceHolder")}
              aria-label={t("signIn.emailPlaceHolder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="portal-auth-input"
            />

            {mode === MODES.PASSWORD && (
              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  placeholder={t("signIn.passwordPlaceHolder")}
                  aria-label={t("signIn.passwordPlaceHolder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="portal-auth-input"
                />

                <span
                  className="password-toggle-icon"
                  onClick={() => setShowPassword((prev) => !prev)}
                  role="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            )}

            <label className="remember-email">
              <input
                type="checkbox"
                checked={rememberEmail}
                onChange={(e) => setRememberEmail(e.target.checked)}
              />
              <span>{t("signIn.rememberEmail")}</span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className={`${loading ? "disabled" : ""}`}
            >
              {loading && !passkeyBusy
                ? t("signIn.loading")
                : t(mode === MODES.PASSWORD ? "signIn.signInButton" : "signIn.sendCodeButton")}
            </button>

            {mode === MODES.CODE && (
              <p className="otp-helper">{t("signIn.codeIntro")}</p>
            )}

            {showPasskeyOption && (
              <div className="passkey-signin">
                <p className="passkey-signin-divider">{t("passkey.or")}</p>
                <button
                  type="button"
                  className={`passkey-signin-button ${loading ? "disabled" : ""}`}
                  onClick={() => runGuarded(handlePasskeySignIn)}
                  disabled={loading}
                >
                  {passkeyBusy ? t("passkey.signInWaiting") : t("passkey.signInButton")}
                </button>
              </div>
            )}

            {mode === MODES.CODE && (
              <button
                type="button"
                className="link-button mt-10"
                onClick={() => switchMode(MODES.PASSWORD)}
              >
                {t("signIn.usePasswordLink")}
              </button>
            )}

            {mode === MODES.PASSWORD && (
              <>
                <button
                  type="button"
                  className="link-button mt-10"
                  onClick={() => switchMode(MODES.FORGOT)}
                >
                  {t("signIn.forgotPasswordQuestion")}
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => switchMode(MODES.CODE)}
                >
                  {t("signIn.useCodeLink")}
                </button>
              </>
            )}

            {mode === MODES.FORGOT && (
              <button
                type="button"
                className="link-button mt-10"
                onClick={() => switchMode(MODES.CODE)}
              >
                {t("signIn.backToSignIn")}
              </button>
            )}

            <button
              type="button"
              className="link-button"
              onClick={() => setIsHelpModalOpen(true)}
            >
              {t("signIn.needHelpLink")}
            </button>
          </form>
        )}

        {passwordPrompt && (
          <form
            className="signin-form signin-form--modes"
            onSubmit={submitWith(handleCreatePassword, allPasswordRulesMet && passwordsMatch)}
            noValidate
          >
            <p className="mb-10">
              {t(
                passwordPrompt.reason === PASSWORD_PROMPT_REASONS.PASSWORD_RESET
                  ? "signIn.createPasswordResetIntro"
                  : "signIn.createPasswordIntro",
              )}
            </p>

            {newPasswordFields}

            <p className="signin-method-note">{t("signIn.createPasswordModeNote")}</p>

            <button
              type="submit"
              className="resetPasswordButton"
              disabled={loading || !allPasswordRulesMet || !passwordsMatch}
            >
              {loading ? t("signIn.loading") : t("signIn.createPasswordButton")}
            </button>
            <button
              type="button"
              className="link-button mt-10"
              onClick={() => runGuarded(handleSkipPassword)}
              disabled={loading}
            >
              {t("signIn.skipPasswordButton")}
            </button>

            <PasswordRules passwordRules={passwordRules} t={t} />
          </form>
        )}

        {passwordReset && (
          <form
            className="signin-form signin-form--modes"
            onSubmit={submitWith(
              handleConfirmPasswordReset,
              resetCode.length === OTP_LENGTH && allPasswordRulesMet && passwordsMatch,
            )}
            noValidate
          >
            <p className="otp-destination">
              {t("signIn.resetCodeSent")} <strong>{email}</strong>.
            </p>

            <SegmentedOtpInput
              value={resetCode}
              onChange={setResetCode}
              idPrefix="reset-code"
              label={t("signIn.resetCodeLabel")}
            />

            {newPasswordFields}

            <button
              type="submit"
              className="resetPasswordButton"
              disabled={
                loading || resetCode.length !== OTP_LENGTH || !allPasswordRulesMet || !passwordsMatch
              }
            >
              {loading ? t("signIn.loading") : t("signIn.resetPasswordButton")}
            </button>

            <PasswordRules passwordRules={passwordRules} t={t} />

            <button
              type="button"
              className="link-button mt-10"
              onClick={() => runGuarded(handleResendResetCode)}
              disabled={loading}
            >
              {t("signIn.resendOtpButton")}
            </button>
            {resendSuccess && (
              <p style={{ color: "green", marginTop: "10px" }}>{t("signIn.resendOtpSuccess")}</p>
            )}
            <button
              type="button"
              className="link-button"
              onClick={() => switchMode(MODES.CODE)}
              disabled={loading}
            >
              {t("signIn.backToSignIn")}
            </button>
          </form>
        )}

        {challenge?.ChallengeName === "NEW_PASSWORD_REQUIRED" && (
          <form
            className="signin-form signin-form--modes"
            onSubmit={submitWith(handleNewPasswordSubmit, allPasswordRulesMet && passwordsMatch)}
            noValidate
          >
            <p className="mb-10">{t("signIn.setNewPasswordMessage")}</p>

            {newPasswordFields}

            <button
              type="submit"
              className="resetPasswordButton"
              disabled={loading || !allPasswordRulesMet || !passwordsMatch}
            >
              {t("signIn.setPasswordButton")}
            </button>

            <PasswordRules passwordRules={passwordRules} t={t} />
          </form>
        )}

        {challenge?.ChallengeName === "EMAIL_OTP" && (
          <form
            className="otp-screen"
            onSubmit={submitWith(handleOtpSubmit, !otpExpired && otpCode.length === otpLength)}
            noValidate
          >
            <h2>{t("signIn.otpTitle")}</h2>
            <p className="otp-destination">
              {t("signIn.otpDestination")} <strong>{email}</strong>.
            </p>
            <p className={`otp-status ${otpExpired ? "otp-status--expired" : ""}`} aria-live="polite">
              {otpExpired
                ? t("signIn.otpExpired")
                : `${t("signIn.otpExpiry")} ${formatCountdown(otpRemainingMs)}.`}
            </p>

            <SegmentedOtpInput
              value={otpCode}
              onChange={handleOtpCodeChange}
              idPrefix="sign-in-otp"
              length={otpLength}
              label={t(
                otpLength === FIRST_FACTOR_OTP_LENGTH
                  ? "signIn.otpInstructionFirstFactor"
                  : "signIn.otpInstruction",
              )}
            />

            <button
              type="submit"
              disabled={loading || otpExpired || otpCode.length !== otpLength}
            >
              {loading ? t("signIn.loading") : t("signIn.verificationButton")}
            </button>

            <p className="otp-helper">{t("signIn.verificationMessage")}</p>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={loading}
              className={`link-button otp-resend ${loading ? "disabled" : ""}`}
            >
              {t("signIn.resendOtpButton")}
            </button>

            {resendSuccess && (
              <p style={{ color: "green", marginTop: "10px" }}>
                {t("signIn.resendOtpSuccess")}
              </p>
            )}
            <button
              type="button"
              className="link-button otp-help"
              onClick={() => setIsHelpModalOpen(true)}
              disabled={loading}
            >
              {t("signIn.otpHelp")}
            </button>
            <button type="button" className="link-button otp-back" onClick={resetForm} disabled={loading}>
              {t("signIn.otpBack")}
            </button>
          </form>
        )}

        <Modal
          isOpen={isHelpModalOpen}
          onClose={() => setIsHelpModalOpen(false)}
        >
          <SignInHelpForm
            initialEmail={email}
            onClose={() => setIsHelpModalOpen(false)}
          />
        </Modal>
      </div>
    </div>
  );
};

const PasswordRules = ({ passwordRules, t }) => (
  <div className="resetPasswordLines">
    <p className="resetPasswordLineTitle">{t("signIn.newPasswordLineTitle")}</p>
    <p className={`resetPasswordLine ${passwordRules.minLength ? "valid" : ""}`}>{t("signIn.newPasswordLine1")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasNumber ? "valid" : ""}`}>{t("signIn.newPasswordLine2")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasSpecialChar ? "valid" : ""}`}>{t("signIn.newPasswordLine3")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasUppercase ? "valid" : ""}`}>{t("signIn.newPasswordLine4")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasLowercase ? "valid" : ""}`}>{t("signIn.newPasswordLine5")}</p>
  </div>
);

export default SignIn;
